import { Link } from 'react-router-dom';
import { SlidersHorizontal, Palette, Languages, Globe2, Loader2, Type, Sparkles } from 'lucide-react';
import SettingCard from '../../components/settings/SettingCard';
import SettingsRow from '../../components/settings/SettingsRow';
import Segmented from '../../components/settings/Segmented';
import Toggle from '../../components/settings/Toggle';
import AutomationPanel from '../../components/settings/AutomationPanel';
import { useSettingsStore } from '../../store/settingsStore';
import { useTranslation } from '../../hooks/useTranslation';
import { useLocale } from '../../hooks/useLocale';
import { REGIONS } from '../../services/localizationService';
import type { AppPreferences } from '../../types/settings.types';

const ACCENTS = ['#171717', '#2563eb', '#7c3aed', '#db2777', '#059669', '#ea580c'];

export default function Preferences() {
  const { data, loading, savePreferences } = useSettingsStore();
  const { t } = useTranslation();
  const { regionConfig, formatShortDate, formatTime, formatNumber, formatLongDate } = useLocale();

  if (loading || !data) {
    return (
      <div className="flex items-center justify-center py-24 text-neutral-400 dark:text-neutral-500">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  const prefs = data.preferences;
  const update = (patch: Partial<AppPreferences>) => savePreferences(patch);
  const now = new Date();

  return (
    <div className="space-y-6">
      <div className="hidden lg:block">
        <h1 className="text-2xl font-bold tracking-tight">{t('settings.preferences.title')}</h1>
        <p className="text-sm text-neutral-500 dark:text-neutral-400">{t('settings.preferences.subtitle')}</p>
      </div>

      <SettingCard title={t('settings.preferences.appearance')} icon={Palette} description={t('settings.preferences.appearanceDesc')}>
        <div className="space-y-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="font-semibold text-sm">{t('settings.preferences.theme')}</p>
              <p className="text-xs text-neutral-500 dark:text-neutral-400">{t('settings.preferences.themeDesc')}</p>
            </div>
            <Segmented
              value={prefs.theme}
              onChange={(theme) => update({ theme })}
              options={[
                { value: 'light', label: t('settings.preferences.light') },
                { value: 'dark', label: t('settings.preferences.dark') },
                { value: 'system', label: t('settings.preferences.system') },
              ]}
            />
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="font-semibold text-sm">{t('settings.preferences.accentColor')}</p>
              <p className="text-xs text-neutral-500 dark:text-neutral-400">{t('settings.preferences.accentColorDesc')}</p>
            </div>
            <div className="flex items-center gap-2">
              {ACCENTS.map((c) => (
                <button
                  key={c}
                  onClick={() => update({ accent: c })}
                  aria-label={`${t('settings.preferences.accentColor')} ${c}`}
                  className={
                    'w-8 h-8 rounded-full transition-all ' +
                    (prefs.accent === c ? 'ring-2 ring-offset-2 ring-neutral-900 scale-110' : 'hover:scale-105')
                  }
                  style={{ backgroundColor: c }}
                />
              ))}
            </div>
          </div>

          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Type className="w-4 h-4 text-neutral-500 dark:text-neutral-400" />
              <p className="font-semibold text-sm">{t('settings.preferences.textSize')}</p>
            </div>
            <Segmented
              value={prefs.fontScale}
              onChange={(fontScale) => update({ fontScale })}
              options={[
                { value: 'small', label: 'S' },
                { value: 'medium', label: 'M' },
                { value: 'large', label: 'L' },
              ]}
            />
          </div>
        </div>
      </SettingCard>

      <SettingCard flush>
        <div className="px-1">
          <RowToggle label={t('settings.preferences.compactMode')} description={t('settings.preferences.compactModeDesc')} checked={prefs.compactMode} onChange={(v) => update({ compactMode: v })} />
          <RowToggle label={t('settings.preferences.reduceMotion')} description={t('settings.preferences.reduceMotionDesc')} checked={prefs.reduceMotion} onChange={(v) => update({ reduceMotion: v })} />
        </div>
      </SettingCard>

      <SettingCard title={t('settings.preferences.languageRegion')} icon={Languages} flush>
        <SettingsRow
          to="/app/settings/preferences/language"
          label={t('settings.preferences.appLanguage')}
          description={t('settings.preferences.appLanguageDesc')}
          trailing={<span className="text-sm text-neutral-500 dark:text-neutral-400">{t(`languages.${prefs.language}`)}</span>}
        />

        {/* Region drives date, time and number formats. */}
        <div className="flex items-center justify-between px-5 py-4 border-t border-neutral-100 dark:border-neutral-800">
          <div>
            <p className="font-semibold text-sm">{t('settings.preferences.region')}</p>
            <p className="text-xs text-neutral-500 dark:text-neutral-400">{t('settings.preferences.regionDesc')}</p>
          </div>
          <select
            value={prefs.region}
            onChange={(e) => update({ region: e.target.value })}
            className="rounded-xl border border-neutral-200 dark:border-neutral-800 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-900/10"
          >
            {Object.keys(REGIONS).map((code) => (
              <option key={code} value={code}>{t(`regions.${code}`)}</option>
            ))}
          </select>
        </div>

        {/* Live preview of the formats the selected region produces. */}
        <div className="px-5 py-4 border-t border-neutral-100 dark:border-neutral-800 bg-neutral-50/60">
          <div className="flex items-center gap-2 mb-3">
            <Globe2 className="w-4 h-4 text-neutral-500 dark:text-neutral-400" />
            <p className="font-semibold text-sm">{t('settings.preferences.formatPreview')}</p>
          </div>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
            <FormatRow label={t('settings.preferences.dateFormat')} value={`${formatShortDate(now)} · ${formatLongDate(now)}`} hint={regionConfig.dateFormat} />
            <FormatRow
              label={t('settings.preferences.timeFormat')}
              value={formatTime(now)}
              hint={regionConfig.hour12 ? t('settings.preferences.hour12') : t('settings.preferences.hour24')}
            />
            <FormatRow label={t('settings.preferences.numberFormat')} value={formatNumber(1234567.89)} />
            <FormatRow
              label={t('settings.preferences.firstDayOfWeek')}
              value={regionConfig.firstDayOfWeek === 'monday' ? t('settings.preferences.weekdayMonday') : t('settings.preferences.weekdaySunday')}
            />
          </dl>
        </div>

        <div className="flex items-center justify-between px-5 py-4 border-t border-neutral-100 dark:border-neutral-800">
          <div>
            <p className="font-semibold text-sm">{t('settings.preferences.startScreen')}</p>
            <p className="text-xs text-neutral-500 dark:text-neutral-400">{t('settings.preferences.startScreenDesc')}</p>
          </div>
          <select
            value={prefs.startScreen}
            onChange={(e) => update({ startScreen: e.target.value as AppPreferences['startScreen'] })}
            className="rounded-xl border border-neutral-200 dark:border-neutral-800 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-900/10"
          >
            <option value="dashboard">{t('nav.dashboard')}</option>
            <option value="messaging">{t('nav.messaging')}</option>
            <option value="social">{t('nav.social')}</option>
            <option value="commerce">{t('nav.commerce')}</option>
          </select>
        </div>
      </SettingCard>

      <AutomationPanel category="appearance" title={t('settings.preferences.appearanceAutomation')} description={t('settings.preferences.appearanceAutomationDesc')} />

      <p className="flex items-center gap-2 text-xs text-neutral-400 dark:text-neutral-500 px-2">
        <Sparkles className="w-3.5 h-3.5" />
        {t('settings.preferences.tip')}
        <Link to="/app/settings/notifications" className="text-neutral-600 dark:text-neutral-300 font-medium hover:underline">{t('nav.notifications')} →</Link>
      </p>

      <div className="flex items-center gap-2 text-neutral-400 dark:text-neutral-500 px-2">
        <SlidersHorizontal className="w-4 h-4" />
        <span className="text-xs">{t('settings.preferences.autoSaved')}</span>
      </div>
    </div>
  );
}

function FormatRow({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3 min-w-0">
      <dt className="text-neutral-500 dark:text-neutral-400 shrink-0">{label}</dt>
      <dd className="font-medium text-neutral-900 dark:text-neutral-50 text-right truncate">
        {value}
        {hint && <span className="ml-2 text-[11px] font-mono text-neutral-400 dark:text-neutral-500">{hint}</span>}
      </dd>
    </div>
  );
}

function RowToggle({ label, description, checked, onChange }: { label: string; description: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <div className="flex items-center justify-between gap-4 px-4 py-4 border-b border-neutral-100 dark:border-neutral-800 last:border-0">
      <div>
        <p className="font-semibold text-sm text-neutral-900 dark:text-neutral-50">{label}</p>
        <p className="text-xs text-neutral-500 dark:text-neutral-400">{description}</p>
      </div>
      <Toggle checked={checked} onChange={onChange} label={label} />
    </div>
  );
}
